import { Component, OnDestroy, OnInit } from '@angular/core';
import { APP_CONFIG } from '@services/config.service';
import { SpecificFields } from '@clients/models/client.model';
import { SharedClientConfigurationService } from '@services/client/sharedClientConfiguration.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';

type SpecificFieldsKeys =
  | 'newRecipeScreensEnable'
  | 'newRecipeSearchScreenEnable'
  | 'newPackScreensEnable'
  | 'newSearchPackScreensEnable'
  | 'newCommentsModerationScreensEnable'
  | 'newUserSearchScreenEnable';

interface URLMapping {
  [key: string]: string;
}

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  standalone: false,
})
export class MenuComponent implements OnInit, OnDestroy {
  panelOpenState = false;
  APP_CONFIG = APP_CONFIG;

  urlMappings: URLMapping = {
    newRecipeScreensEnable: '/recipes/new',
    newRecipeSearchScreenEnable: '/recipes/search',
    newPackScreensEnable: '/references/packs/new',
    newSearchPackScreensEnable: '/references/packs/search',
    newCommentsModerationScreensEnable: '/moderation/comments',
    newUserSearchScreenEnable: '/users/search',
  };

  links: { [K in SpecificFieldsKeys]: string } = {
    newRecipeScreensEnable: '#/recipes/new',
    newRecipeSearchScreenEnable: '#/search',
    newPackScreensEnable: '#/references/packs/new',
    newSearchPackScreensEnable: '#/references/packs/search',
    newCommentsModerationScreensEnable: '#/comments/',
    newUserSearchScreenEnable: '#/users/search',
  };

  destroy$ = new Subject<void>();

  constructor(
    private readonly sharedClientConfigService: SharedClientConfigurationService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.sharedClientConfigService.specificFields$.pipe(takeUntil(this.destroy$)).subscribe((specificFields: SpecificFields) => {
      Object.keys(this.urlMappings).forEach((key: string) => {
        this.setLink(key as SpecificFieldsKeys, specificFields?.[key as SpecificFieldsKeys]);
      });
    });
  }

  handleUserRedirect(): void {
    this.handleRedirect(this.links.newUserSearchScreenEnable);
  }

  handleRecipeSearchRedirect(): void {
    this.handleRedirect(this.links.newRecipeSearchScreenEnable);
  }

  handlePackRedirect(): void {
    this.handleRedirect(this.links.newPackScreensEnable);
  }

  handleSearchPackRedirect(): void {
    this.handleRedirect(this.links.newSearchPackScreensEnable);
  }

  handleCommentRedirect(): void {
    this.handleRedirect(this.links.newCommentsModerationScreensEnable);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  urlInclude(url: string): boolean {
    return window.location.href.includes(url);
  }

  get isModerationActive(): boolean {
    return ['moderation/comments', '#/communities-recipies', '/communities-recipes'].some((url) => this.urlInclude(url));
  }

  get isAdministrationActive(): boolean {
    return ['#/references/list', '#/references/export', '#/references/semantic', '#/translations/import', '#/audit/search'].some((url) =>
      this.urlInclude(url),
    );
  }

  private handleRedirect(link: string): void {
    this.router.navigateByUrl(link).then((r) => r);
  }

  private setLink(field: SpecificFieldsKeys, isEnabled: string | number | boolean | undefined): void {
    let base = this.urlMappings[field];
    if (field === 'newCommentsModerationScreensEnable') {
      base = isEnabled ? '/moderation/comments' : '#/comments/';
      this.links[field] = base;
      return;
    }
    if (field === 'newRecipeSearchScreenEnable') {
      base = isEnabled ? '/recipes/search' : '#/search';
      this.links[field] = base;
      return;
    }
    this.links[field] = isEnabled ? base : `#${base}`;
  }
}
